import React, {
  FC,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import qs from 'qs';
import cx from 'classnames';
import { routes, URL } from 'appConstants';
import { ButtonV4, Link } from 'components';
import { useRequest } from 'hooks';
import {
  _has_t_back,
  _t,
  _t_back,
  dateToUiFormat,
  getEventsFromDate,
  getEventCardTag,
  hasEventDetails,
  isEventFromCalendarPeriod,
  isEventRegistrationOpen,
  matchEventFilters,
  openApplicationForm,
  sortEventsByDate,
  validateEmptyParams,
} from 'utils';
import type { EventFilterId } from 'utils';
import type { Event, GetResponse } from 'types';
import { RequestStatus } from 'types';

import iconSearch from 'assets/img/main-v4/icon-search.svg';
import iconFilter from 'assets/img/main-v4/icon-filter.svg';
import iconPlus from 'assets/img/main-v4/icon-plus.svg';
import eventHero from 'assets/img/main-v4/event-hero.png';

import styles from './styles.module.scss';

type Props = {
  className?: string,
};

type FilterId = EventFilterId;

const FILTERS: { id: FilterId, label: string }[] = [
  { id: 'exhibitions', label: 'Выставки' },
  { id: 'forums', label: 'Форумы' },
  { id: 'lessons', label: 'Лекции' },
  { id: 'tours', label: 'Экскурсии' },
  { id: 'missions', label: 'Бизнес-миссии' },
];

const PAGE_SIZE = 7;

const formatMeta = (item: Event) => {
  const start = dateToUiFormat(item.date_of_event_start || '').slice(0, 5);
  const end = dateToUiFormat(item.date_of_event_end || '').slice(0, 5);
  const dates = [start, end].filter(Boolean).join(' - ');
  const location = _t_back(item, 'location') || '';
  return [dates, location].filter(Boolean).join('\n');
};

const MainEvents: FC<Props> = ({ className }) => {
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<FilterId[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data, status, request } = useRequest<GetResponse<Event[]>>({
    errback: () => undefined,
  });

  const loadEvents = (search = '') => {
    request({
      method: 'get',
      url: URL.EVENTS.GET,
      params: validateEmptyParams({
        params: {
          search,
          start_filter_date: getEventsFromDate(),
          limit: 100,
          offset: 0,
        },
        emptyParamsToDelete: ['search'],
      }),
      paramsSerializer: (value) => qs.stringify(value, { indices: false }),
    });
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFilter = (id: FilterId) => {
    setActiveFilters((prev) => (
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    ));
  };

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    loadEvents(query);
  };

  const cards = useMemo(() => (
    sortEventsByDate(data?.results || [])
      .filter((item) => _has_t_back(item))
      .filter((item) => isEventFromCalendarPeriod(item))
      .filter((item) => matchEventFilters(item, activeFilters))
      .slice(0, PAGE_SIZE)
      .map((item, index) => ({
        id: String(item.id),
        slug: item.slug,
        tag: _t(getEventCardTag(item)),
        title: _t_back(item, 'title'),
        meta: formatMeta(item),
        featured: index === 0,
        canLink: hasEventDetails(item),
        canRegister: isEventRegistrationOpen(item),
      }))
  ), [data, activeFilters]);

  return (
    <section className={cx(styles.wrap, className)}>
      <div className={styles.inner}>
        <div className={styles.intro}>
          <div className={styles.head}>
            <h2 className={styles.title}>{_t('Календарь событий')}</h2>
            <ButtonV4
              to={routes.events.root}
              label="Смотреть все мероприятия"
              variant="outline"
              tone="onLight"
              size="sm"
              className={styles.allBtn}
            />
          </div>

          <div className={styles.toolbar}>
            <form className={styles.search} onSubmit={onSearchSubmit}>
              <input
                className={styles.searchInput}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={_t('Введите наименование или дату мероприятия')}
                aria-label={_t('Поиск мероприятий')}
              />
              <button type="submit" className={styles.searchBtn} aria-label={_t('Искать')}>
                <img src={iconSearch} alt="" width={14} height={14} />
              </button>
            </form>

            <div className={styles.filters}>
              <div
                id="main-events-filters"
                className={cx(styles.checkList, !filtersOpen && styles.checkListHidden)}
              >
                {FILTERS.map((filter) => {
                  const checked = activeFilters.includes(filter.id);
                  const inputId = `main-events-filter-${filter.id}`;
                  return (
                    <label key={filter.id} className={styles.check} htmlFor={inputId}>
                      <span className={cx(styles.box, checked && styles.boxChecked)} aria-hidden>
                        {checked && <span className={styles.tick} />}
                      </span>
                      <input
                        id={inputId}
                        type="checkbox"
                        className={styles.nativeCheck}
                        checked={checked}
                        onChange={() => toggleFilter(filter.id)}
                      />
                      <span className={styles.checkLabel}>{_t(filter.label)}</span>
                    </label>
                  );
                })}
              </div>

              <button
                type="button"
                className={cx(styles.filterBtn, filtersOpen && styles.filterBtnOpen)}
                aria-expanded={filtersOpen}
                aria-controls="main-events-filters"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                <img src={iconFilter} alt="" width={13} height={12} />
                <span>{_t('Настроить фильтр')}</span>
              </button>
            </div>
          </div>
        </div>

        <ul className={styles.grid}>
          {cards.map((card) => {
            const to = `${routes.events.root}/${card.slug || card.id}`;
            return (
              <li
                key={card.id}
                className={cx(styles.card, card.featured && styles.cardFeatured)}
                style={card.featured ? { ['--event-hero' as string]: `url(${eventHero})` } : undefined}
              >
                <div className={styles.cardTop}>
                  <span className={styles.tag}>{card.tag}</span>
                </div>
                <div className={styles.cardBody}>
                  {card.canLink ? (
                    <Link to={to} className={styles.cardTitle}>{card.title}</Link>
                  ) : (
                    <h3 className={styles.cardTitle}>{card.title}</h3>
                  )}
                  <p className={styles.cardMeta}>{card.meta}</p>
                  {card.canRegister ? (
                    <ButtonV4
                      type="button"
                      label="Регистрация"
                      variant="outline"
                      tone={card.featured ? 'frosted' : 'onLight'}
                      iconSrc={iconPlus}
                      className={styles.regBtn}
                      onClick={() => openApplicationForm({
                        type: 'event',
                        id: card.id,
                        title: card.title,
                        path: to,
                      })}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        {status === RequestStatus.SUCCESS && !cards.length ? (
          <p className={styles.cardMeta}>{_t('Мероприятия не найдены')}</p>
        ) : null}
      </div>
    </section>
  );
};

export default MainEvents;
